import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

/**
 * Scroll-driven parallax wrapper — same effect as the hero image.
 *
 * The wrapped element (image + its frame) translates as ONE UNIT — slower
 * upward than the page → text/buttons glide UP over it. The image stays
 * fixed inside its frame; nothing is scaled or cropped. The whole block
 * just moves at a slower vertical speed than the surrounding text.
 *
 * Works on every viewport. Respects `prefers-reduced-motion`.
 */
export default function MobileParallax({
  children,
  className = "",
  intensity = 40,
  dim = 0.94,
  ...rest
}) {
  const ref = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: ref,
    // Parallax kicks in only AFTER the element reaches the top of the screen.
    // Before that, image and text move evenly with the page (no parallax).
    offset: ["start start", "end start"],
  });

  // Image starts at its natural position (no upward lift → never overlaps
  // section titles above it) and translates DOWN as the user scrolls past
  // the top of the screen. Result: image moves slower upward than the page
  // → text rolls over it. Before the section reaches the top, image and
  // text move evenly with the page.
  const y = useTransform(scrollYProgress, [0, 1], [0, intensity]);
  const brightness = useTransform(
    scrollYProgress,
    [0, 0.45, 0.55, 1],
    [dim, 1, 1, dim]
  );
  const filter = useTransform(brightness, (b) => `brightness(${b})`);

  if (prefersReducedMotion) {
    return (
      <div ref={ref} className={className} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      style={{ y, filter, willChange: "transform, filter" }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}
